import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Dialog, Box, IconButton, Fade, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnimatedOrb from "../../dashboard/TaskGuide/AnimatedOrb";
import SpeechBubble from "../../dashboard/TaskGuide/SpeechBubble";
import Button from "../../input/button";
import GradientButton from "../../input/GradientButton";
import useCreateTask from "../../../hooks/tasks/useCreateTask";
import EmojiPickerDialog from "../../input/EmojiPickerDialog";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import { Emoji } from "emoji-picker-react";
import Input from "../../input";
import Ticker from "../../ticker";
import LabelContainer from "../../labels/LabelContainer";
import LabelsDialog from "../../labels/LabelsDialog";
import NotesContainer from "../taskInputs/notes/NotesContainer";
import RecurringTasksContainer from "../taskInputs/recurringTasks/RecurringTasksContainer";
import DeadlineContainer from "../taskInputs/DeadlineContainer";
import TargetToggleContainer from "../taskInputs/TargetToggleContainer";
import { Capacitor } from "@capacitor/core";
import useTaskUsage from "../../../hooks/tasks/useTaskUsage";
import useSubscribe from "../../../hooks/subscription/useSubscribe";
import TaskUsage from "../../account/TaskUsage";

// ─── Text maps for ticker values ─────────────────────────────────────────
const importanceTextMap = {
  1: "Extremely unimportant",
  2: "Very unimportant",
  3: "Unimportant",
  4: "Fairly unimportant",
  5: "Neutral",
  6: "Quite important",
  7: "Important",
  8: "Very important",
  9: "Extremely important",
  10: "Critical",
};

const difficultyTextMap = {
  1: "Extremely easy",
  2: "Very easy",
  3: "Easy",
  4: "Fairly easy",
  5: "Neutral",
  6: "Quite hard",
  7: "Hard",
  8: "Very hard",
  9: "Extremely hard",
  10: "Yikes",
};

const funTextMap = {
  1: "A bad time",
  2: "Not fun at all",
  3: "Not fun",
  4: "A little boring",
  5: "Neutral",
  6: "A little fun",
  7: "Fun",
  8: "Very fun",
  9: "Extremely fun",
  10: "Too much fun",
};

// ─── Step ordering ───────────────────────────────────────────────────────
const STEPS = [
  "welcome",
  "task-usage",
  "task-name",
  "task-importance",
  "task-difficulty",
  "task-fun",
  "task-labels",
  "task-notes",
  "task-recurrence",
  "task-deadline",
  "task-target",
  "completed",
];

// ─── Orb prompts per step ────────────────────────────────────────────────
const ORB_PROMPTS = {
  welcome:
    "Hi, I'm Ori, your focus guide. Looks like you've not got any tasks at the moment. Let's create your first task together.",
  "task-name": "Every task needs a name. What would you like to call it?",
  "task-importance":
    "How important is this task? This helps me prioritise it for you.",
  "task-difficulty":
    "How difficult will this be? I'll use this to match tasks to your energy levels.",
  "task-fun":
    "How much fun will this task be? I'll suggest it when you're in the right mood.",
  "task-labels":
    "Labels help you organise your tasks. Add some if you'd like, or skip ahead.",
  "task-notes":
    "Notes let you attach extra info to your task. Add some, or skip this step.",
  "task-recurrence":
    "Does this task repeat on a schedule? Set it up here if so.",
  "task-deadline":
    "Does this task have a deadline? I'll remind you when it's approaching.",
  "task-target":
    "Want to focus on this task? Targeting it keeps it front and centre.",
  completed: "Your task has been created! You're all set.",
};

// ─── Dynamic Ori prompt for task-usage step ──────────────────────────────
const getTaskUsagePrompt = (remaining, taskLimit) => {
  if (remaining === 0) {
    return "You've used all your free tasks. Subscribe to Mindstack to create unlimited tasks and unlock all features.";
  }
  if (remaining === 1) {
    return "You've got 1 free task left! Make it count, or subscribe to unlock unlimited tasks.";
  }
  if (remaining <= 3) {
    return `You've only got ${remaining} free tasks remaining. You're getting close to the limit!`;
  }
  if (remaining === taskLimit) {
    return `You've got ${remaining} free tasks to use. Let's put one to good use!`;
  }
  return `You have ${remaining} free tasks remaining. Let's create a new one!`;
};

/**
 * Task Helper Dialog — A guided task creation experience powered by Ori the orb.
 * Styled consistently with the Focus Guide dialog.
 */
const TaskHelperDialog = ({ open, onClose, onTaskCreated, skipWelcome = false }) => {
  const { createTask, saving } = useCreateTask();
  const isMobile = Capacitor.isNativePlatform();
  const tasks = useSelector((state) => state.tasks.taskSet);
  const hasTasks = Object.keys(tasks).length > 0;
  const { subscription } = useSelector((state) => state.user);
  const isFreeUser = subscription?.plan === "free";
  const { tasksUsed, taskLimit } = useTaskUsage();
  const remaining = Math.max(taskLimit - tasksUsed, 0);

  // ─── Step navigation state ──────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState("welcome");
  const [fadeIn, setFadeIn] = useState(true);
  const [pendingStep, setPendingStep] = useState(null);

  // ─── Task data state ────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("");
  const [importance, setImportance] = useState(5);
  const [difficulty, setDifficulty] = useState(5);
  const [fun, setFun] = useState(5);
  const [labels, setLabels] = useState([]);
  const [notes, setNotes] = useState({});
  const [frequency, setFrequency] = useState();
  const [deadline, setDeadline] = useState();
  const [targeted, setTargeted] = useState(false);
  const [isRecurring, setRecurring] = useState(false);
  const [hasDeadline, setHasDeadline] = useState(false);

  // ─── Labels dialog state ────────────────────────────────────────────
  const [showLabelsDialog, setShowLabelsDialog] = useState(false);

  // ─── Emoji picker dialog state ──────────────────────────────────────
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  // ─── Notes state ────────────────────────────────────────────────────
  const noteInputValueRef = useRef();
  const [noteInputError, setNoteInputError] = useState("");

  // ─── Reset on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      const skipToStep = skipWelcome || hasTasks
        ? (isFreeUser ? "task-usage" : "task-name")
        : "welcome";
      setCurrentStep(skipToStep);
      setFadeIn(true);
      setName("");
      setEmoji("");
      setImportance(5);
      setDifficulty(5);
      setFun(5);
      setLabels([]);
      setNotes({});
      setFrequency(undefined);
      setDeadline(undefined);
      setTargeted(false);
      setRecurring(false);
      setHasDeadline(false);
      setNoteInputError("");
    }
  }, [open, skipWelcome]);

  // ─── Fade transition ───────────────────────────────────────────────
  const transitionTo = useCallback((step) => {
    setFadeIn(false);
    setPendingStep(step);
  }, []);

  useEffect(() => {
    if (!fadeIn && pendingStep) {
      const timer = setTimeout(() => {
        setCurrentStep(pendingStep);
        setPendingStep(null);
        setFadeIn(true);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [fadeIn, pendingStep]);

  // ─── Create task ───────────────────────────────────────────────────
  const onCreateTask = useCallback(async () => {
    const newTask = {
      name,
      notes,
      attributes: { importance, difficulty, fun },
      labels,
      targeted,
      deadline: deadline ? deadline.unix() : undefined,
      frequency,
      emoji,
    };

    try {
      await createTask(newTask);
      transitionTo("completed");
    } catch (error) {
      console.error("Failed to create task:", error);
    }
  }, [
    name,
    notes,
    importance,
    difficulty,
    fun,
    labels,
    targeted,
    deadline,
    frequency,
    emoji,
    createTask,
    transitionTo,
  ]);

  // ─── Step navigation ──────────────────────────────────────────────
  const getNextStep = useCallback(
    (step) => {
      const idx = STEPS.indexOf(step);
      if (step === "task-recurrence" && isRecurring) {
        return "task-target";
      }
      let nextIdx = idx + 1;
      // Skip task-usage for subscribed users
      if (STEPS[nextIdx] === "task-usage" && !isFreeUser) {
        nextIdx++;
      }
      if (nextIdx < STEPS.length) return STEPS[nextIdx];
      return null;
    },
    [isRecurring, isFreeUser],
  );

  const getPrevStep = useCallback(
    (step) => {
      const idx = STEPS.indexOf(step);
      if (step === "task-target" && isRecurring) {
        return "task-recurrence";
      }
      let prevIdx = idx - 1;
      // Skip task-usage for subscribed users
      if (STEPS[prevIdx] === "task-usage" && !isFreeUser) {
        prevIdx--;
      }
      if (prevIdx >= 0) return STEPS[prevIdx];
      return null;
    },
    [isRecurring, isFreeUser],
  );

  const handleNext = useCallback(() => {
    if (currentStep === "task-target") {
      onCreateTask();
      return;
    }
    if (currentStep === "completed") {
      onClose();
      onTaskCreated?.();
      return;
    }
    const next = getNextStep(currentStep);
    if (next) transitionTo(next);
  }, [
    currentStep,
    getNextStep,
    transitionTo,
    onCreateTask,
    onClose,
    onTaskCreated,
  ]);

  const shouldSkipWelcome = skipWelcome || hasTasks;

  const handleBack = useCallback(() => {
    const prev = getPrevStep(currentStep);
    if (prev && !(prev === "welcome" && shouldSkipWelcome)) {
      transitionTo(prev);
    } else {
      onClose();
    }
  }, [currentStep, getPrevStep, transitionTo, onClose, shouldSkipWelcome]);

  const handleCreateAnother = useCallback(() => {
    setName("");
    setEmoji("");
    setImportance(5);
    setDifficulty(5);
    setFun(5);
    setLabels([]);
    setNotes({});
    setFrequency(undefined);
    setDeadline(undefined);
    setTargeted(false);
    setRecurring(false);
    setHasDeadline(false);
    setNoteInputError("");
    transitionTo(isFreeUser ? "task-usage" : "task-name");
  }, [transitionTo, isFreeUser]);

  // ─── Subscribe handler (mobile bypasses Subscribe page) ────────────
  const { subscribe, subscribing } = useSubscribe({
    onSuccess: () => transitionTo("task-name"),
  });

  const handleSubscribe = useCallback(async () => {
    const result = await subscribe();
    // On web, close the dialog since we're navigating to /subscribe
    if (result?.navigated) {
      onClose();
    }
  }, [subscribe, onClose]);

  // ─── Can proceed? ─────────────────────────────────────────────────
  const atTaskLimit = currentStep === "task-usage" && remaining === 0;
  const canProceed =
    currentStep === "task-name" ? name.trim().length > 0 : !atTaskLimit;
  const showBack = currentStep !== "completed" && (shouldSkipWelcome ? true : currentStep !== "welcome");
  const showNext = currentStep !== "completed" && currentStep !== "welcome" && !atTaskLimit;

  // ─── Notes helpers ────────────────────────────────────────────────
  const onAddNote = (noteText) => {
    if (noteText !== "") {
      const newNotes = { ...notes };
      const newNoteId = +new Date();
      newNotes[newNoteId] = { text: noteText, id: newNoteId, completed: false };
      setNotes(newNotes);
    }
  };

  const onNoteStatusChange = ({ id, status }) => {
    const newNotes = { ...notes };
    newNotes[id].completed = status;
    setNotes(newNotes);
  };

  const onNoteDeleted = (id) => {
    const newNotes = { ...notes };
    delete newNotes[id];
    setNotes(newNotes);
  };

  // ─── Label helpers ────────────────────────────────────────────────
  const onLabelsChange = (newLabels) => setLabels(newLabels);

  const onLabelRemoved = (labelId) => {
    const newLabels = [...labels];
    newLabels.splice(labels.indexOf(labelId), 1);
    setLabels(newLabels);
  };

  // ─── Orb intensity ───────────────────────────────────────────────
  const orbIntensity = currentStep === "completed" ? "celebrating" : "idle";

  // ─── Orb prompt (with dynamic task-usage) ─────────────────────────
  const currentOrbPrompt = currentStep === "task-usage"
    ? getTaskUsagePrompt(remaining, taskLimit)
    : ORB_PROMPTS[currentStep];

  // ─── Next button label ────────────────────────────────────────────
  const getNextLabel = () => {
    if (currentStep === "task-target") return saving ? "" : "Create task";
    return "Next";
  };

  // ─── Render step content (below the orb/speech bubble) ────────────
  const renderStepContent = () => {
    switch (currentStep) {
      case "welcome":
        return null;

      case "task-usage":
        return (
          <Box className="task-helper-dialog__content">
            <TaskUsage
              onSubscribe={handleSubscribe}
              subscribing={subscribing}
            />
          </Box>
        );

      case "task-name":
        return (
          <Box className="task-helper-dialog__content">
            <Box className="task-helper-dialog__name-input">
              <IconButton
                className="task-helper-dialog__emoji-button"
                onClick={() => setEmojiPickerOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                {emoji ? (
                  <Emoji unified={emoji} set="apple" size={28} />
                ) : (
                  <AddReactionIcon
                    sx={{ width: 28, height: 28, color: "rgba(0,0,0,0.35)" }}
                  />
                )}
              </IconButton>
              <Input
                placeholder="Task Name"
                labelText="Task Name"
                onChange={(e) => setName(e.target.value)}
                value={name}
              />
            </Box>
            <EmojiPickerDialog
              open={emojiPickerOpen}
              onClose={() => setEmojiPickerOpen(false)}
              onEmojiSelect={(unified) => {
                setEmoji(unified);
                setEmojiPickerOpen(false);
              }}
            />
          </Box>
        );

      case "task-importance":
        return (
          <Box className="task-helper-dialog__content">
            <Box className="task-helper-dialog__ticker-container">
              <Ticker
                name="Importance"
                value={importance}
                maxValue={10}
                hideLabel
                onValueChange={(increment) =>
                  setImportance(importance + increment)
                }
              />
            </Box>
            <Typography variant="h3" sx={{ textAlign: "center", mt: 1 }}>
              {importanceTextMap[importance]}
            </Typography>
          </Box>
        );

      case "task-difficulty":
        return (
          <Box className="task-helper-dialog__content">
            <Box className="task-helper-dialog__ticker-container">
              <Ticker
                name="Difficulty"
                value={difficulty}
                maxValue={10}
                hideLabel
                onValueChange={(increment) =>
                  setDifficulty(difficulty + increment)
                }
              />
            </Box>
            <Typography variant="h3" sx={{ textAlign: "center", mt: 1 }}>
              {difficultyTextMap[difficulty]}
            </Typography>
          </Box>
        );

      case "task-fun":
        return (
          <Box className="task-helper-dialog__content">
            <Box className="task-helper-dialog__ticker-container">
              <Ticker
                name="Fun"
                value={fun}
                maxValue={10}
                hideLabel
                invertColours
                onValueChange={(increment) => setFun(fun + increment)}
              />
            </Box>
            <Typography variant="h3" sx={{ textAlign: "center", mt: 1 }}>
              {funTextMap[fun]}
            </Typography>
          </Box>
        );

      case "task-labels":
        return (
          <Box className="task-helper-dialog__content">
            <LabelContainer
              editPanelIsOpen={showLabelsDialog}
              selectedLabels={labels}
              onChangeEditLabelPanelVisibility={(visibility) =>
                setShowLabelsDialog(visibility)
              }
            />
            {showLabelsDialog && (
              <LabelsDialog
                open={showLabelsDialog}
                onClose={() => setShowLabelsDialog(false)}
                selectedLabels={labels}
                onSelectedLabelsChange={onLabelsChange}
                onLabelRemoved={onLabelRemoved}
              />
            )}
          </Box>
        );

      case "task-notes":
        return (
          <Box className="task-helper-dialog__content">
            <NotesContainer
              inline
              onAddNote={onAddNote}
              notes={notes}
              onNoteStatusChange={onNoteStatusChange}
              onNoteDeleted={onNoteDeleted}
              noteInputValueRef={noteInputValueRef}
              noteInputError={noteInputError}
              onNoteInputErrorChange={setNoteInputError}
            />
          </Box>
        );

      case "task-recurrence":
        return (
          <Box className="task-helper-dialog__content">
            <RecurringTasksContainer
              frequency={frequency}
              isRecurring={isRecurring}
              setRecurring={setRecurring}
              setFrequency={setFrequency}
              hasDeadline={false}
              setHasDeadline={() => {}}
              setDeadline={() => {}}
            />
          </Box>
        );

      case "task-deadline":
        return (
          <Box className="task-helper-dialog__content">
            <DeadlineContainer
              deadline={deadline}
              setHasDeadline={setHasDeadline}
              hasDeadline={hasDeadline}
              setDeadline={setDeadline}
              isRecurring={false}
              setRecurring={() => {}}
              setFrequency={() => {}}
            />
          </Box>
        );

      case "task-target":
        return (
          <Box className="task-helper-dialog__content">
            {isMobile ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mb: 2 }}
              >
                Targeted tasks are given higher priority in your stack.
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mb: 2 }}
              >
                Targeted tasks are separated from your backlog so you can focus
                on them.
              </Typography>
            )}
            <Box className="task-helper-dialog__ticker-container">
              <TargetToggleContainer
                targeted={targeted}
                setTargeted={setTargeted}
              />
            </Box>
          </Box>
        );

      case "completed":
        return (
          <Box className="task-helper-dialog__content">
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 1 }}
            >
              Keep adding tasks as they come to mind — Mindstack works best when
              it knows everything on your plate.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: "task-guide-dialog task-helper-dialog",
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header — back + close buttons */}
      <Box className="task-guide-dialog__header">
        {showBack && (
          <IconButton
            onClick={handleBack}
            className="task-guide-dialog__back"
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <IconButton
          onClick={onClose}
          className="task-guide-dialog__close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box className="task-guide-dialog__body scroller">
        <Fade in={fadeIn} timeout={250}>
          <Box className="task-guide-dialog__step-container">
            <Box className="task-guide-step">
              <AnimatedOrb size="large" intensity={orbIntensity} />
              <SpeechBubble>
                <Typography
                  variant="body1"
                  sx={{ margin: 0, textAlign: "center" }}
                >
                  {currentOrbPrompt}
                </Typography>
              </SpeechBubble>

              {renderStepContent()}
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* Footer */}
      <Box className="task-guide-dialog__footer">
        {currentStep === "welcome" && (
          <GradientButton text="Let's go!" onClick={handleNext} />
        )}
        {atTaskLimit && (
          <GradientButton
            text="Subscribe to Mindstack"
            onClick={handleSubscribe}
            loading={subscribing}
          />
        )}
        {currentStep === "completed" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <Box
              className="task-guide-step__create-another-button"
              onClick={handleCreateAnother}
            >
              <Typography
                variant="body2"
                sx={{ margin: 0, fontWeight: 500 }}
              >
                + Create another task
              </Typography>
            </Box>
            <GradientButton
              text="Done"
              onClick={() => {
                onClose();
                onTaskCreated?.();
              }}
            />
          </Box>
        )}
        {showNext && (
          <Button
            text={getNextLabel()}
            onClick={handleNext}
            disabled={!canProceed}
            loading={currentStep === "task-target" && saving}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default TaskHelperDialog;
